import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  Staff,
  Car,
  TokenData,
  PaginatedResponse,
  PreviousStaffResponse,
  ConfirmPreviousStaffResponse,
} from "../Component/OtherPages/Participation/ParticipationManagement/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
})


api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    Object.assign(config.headers, getExhibitionHeader());
    return config;
  },
  (error) => Promise.reject(error)
);

export const StaffCarService = {
  generateOTP: async (token: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/exhibitorsaccess/staff-car/generate-otp/`, {
      token,
    })
    return response.data
  },

  verifyOTP: async (
    token: string,
    otp_code: string,
  ): Promise<{ message: string; verified: boolean; token_data: TokenData }> => {
    const response = await api.post(`/api/exhibitorsaccess/staff-car/verify-otp/`, {
      token,
      otp_code,
    })
    return response.data
  },

  verifyToken: async (token: string): Promise<TokenData & { otp_verified: boolean }> => {
    const response = await api.get(`/api/exhibitorsaccess/staff-car/verify-token/?token=${token}`)
    return response.data
  },

  getStaff: async (token: string, page = 1, pageSize = 10, search = ""): Promise<PaginatedResponse<Staff>> => {
    let url = `/api/exhibitorsaccess/staff-car/staff/?token=${token}&page=${page}&page_size=${pageSize}`
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    const response = await api.get(url)
    return response.data
  },

  createStaff: async (token: string, data: FormData): Promise<Staff> => {
    const response = await api.post(`/api/exhibitorsaccess/staff-car/staff/create/?token=${token}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  updateStaff: async (token: string, id: number, data: FormData): Promise<Staff> => {
    const response = await api.put(`/api/exhibitorsaccess/staff-car/staff/${id}/update/?token=${token}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  deleteStaff: async (token: string, id: number): Promise<void> => {
    await api.delete(`/api/exhibitorsaccess/staff-car/staff/${id}/delete/?token=${token}`)
  },

  // Previous staff — lets the focal person bring back staff who represented
  // this exhibitor in a previous exhibition instead of re-entering their
  // details and creating a duplicate Person record.
  getPreviousStaff: async (token: string, search = ""): Promise<PreviousStaffResponse> => {
    const params = new URLSearchParams({ token })
    if (search) params.set("search", search)
    const response = await api.get(`/api/exhibitorsaccess/staff-car/staff/previous/?${params.toString()}`)
    return response.data
  },

  confirmPreviousStaff: async (token: string, personIds: number[]): Promise<ConfirmPreviousStaffResponse> => {
    const response = await api.post(
      `/api/exhibitorsaccess/staff-car/staff/previous/confirm/?token=${token}`,
      { token, person_ids: personIds },
    )
    return response.data
  },

  getCars: async (token: string, page = 1, pageSize = 10): Promise<PaginatedResponse<Car>> => {
    const response = await api.get(
      `/api/exhibitorsaccess/staff-car/cars/?token=${token}&page=${page}&page_size=${pageSize}`,
    )
    return response.data
  },

  createCar: async (
    token: string,
    data: Omit<Car, "id" | "created_at" | "created_by" | "updated_by">,
  ): Promise<Car> => {
    const response = await api.post(`/api/exhibitorsaccess/staff-car/cars/create/?token=${token}`, data)
    return response.data
  },

  updateCar: async (
    token: string,
    id: number,
    data: Omit<Car, "id" | "created_at" | "created_by" | "updated_by">,
  ): Promise<Car> => {
    const response = await api.put(`/api/exhibitorsaccess/staff-car/cars/${id}/update/?token=${token}`, data)
    return response.data
  },

  deleteCar: async (token: string, id: number): Promise<void> => {
    await api.delete(`/api/exhibitorsaccess/staff-car/cars/${id}/delete/?token=${token}`)
  },
}
